import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, FileDown } from "lucide-react";
import api, { download, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty, BasisToggle } from "../components/kit";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const KINDS = [
  ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["staff", "Staff"], ["store_comparison", "Store comparison"],
  ["kpi", "KPI"], ["category", "Category"], ["gms", "GMS"], ["target_vs_actual", "Target vs Actual"], ["returns", "Returns"],
];

export default function Reports() {
  const { stores, storeId, month, basis, setBasis } = useAuth();
  const [kind, setKind] = useState("staff");
  const [sid, setSid] = useState(storeId !== "all" ? storeId : stores[0]?.id);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [staff, setStaff] = useState([]);
  const [cats, setCats] = useState([]);
  const [fl, setFl] = useState({ staff_id: "all", category: "all", date: "" });
  const params = { month, basis, store_id: kind === "store_comparison" ? undefined : sid,
    staff_id: fl.staff_id !== "all" ? fl.staff_id : undefined, category: fl.category !== "all" ? fl.category : undefined,
    date: fl.date || undefined };

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    if (sid) api.get("/users", { params: { role: "staff", store_id: sid } }).then((r) => setStaff(r.data));
  }, [sid]);

  useEffect(() => {
    setD(null);
    setErr("");
    api.get(`/reports/${kind}`, { params: { ...params, format: "json" } }).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [kind, sid, month, basis, fl]); // eslint-disable-line react-hooks/exhaustive-deps

  const exp = async (format) => {
    try { await download(`/reports/${kind}`, { ...params, format }); toast.success("Report downloaded"); }
    catch (e) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow={month} title="Reports" testid="reports-title"
        right={<div className="flex gap-2">
          <Button variant="outline" onClick={() => exp("xlsx")} data-testid="export-excel-button"><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel</Button>
          <Button variant="outline" onClick={() => exp("csv")} data-testid="export-csv-button"><FileDown className="w-4 h-4 mr-2" /> CSV</Button>
          <Button onClick={() => exp("pdf")} className="bg-[#011E38]" data-testid="export-pdf-button"><FileText className="w-4 h-4 mr-2" /> PDF</Button>
        </div>} />
      <div className="flex flex-wrap gap-2" data-testid="report-kind-tabs">
        {KINDS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`report-kind-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border ${kind === k ? "bg-[#011E38] text-white border-[#011E38]" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
        {kind !== "store_comparison" && stores.length > 1 && (
          <Select value={sid} onValueChange={setSid}>
            <SelectTrigger className="w-56 ml-auto" data-testid="report-store-select"><SelectValue /></SelectTrigger>
            <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="report-filters">
        <BasisToggle basis={basis} setBasis={setBasis} />
        <Select value={fl.staff_id} onValueChange={(v) => setFl({ ...fl, staff_id: v })}>
          <SelectTrigger className="w-48 h-10" data-testid="report-staff-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All staff</SelectItem>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fl.category} onValueChange={(v) => setFl({ ...fl, category: v })}>
          <SelectTrigger className="w-44 h-10" data-testid="report-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={fl.date} onChange={(e) => setFl({ ...fl, date: e.target.value })} className="w-44 h-10" data-testid="report-date-filter" />
        {fl.date && <Button variant="ghost" size="sm" onClick={() => setFl({ ...fl, date: "" })} data-testid="report-clear-date">Clear date</Button>}
      </div>
      {err ? <Empty>{err}</Empty> : !d ? <Loading /> : (
        <div className="bs-card overflow-x-auto">
          <div className="px-5 py-4 border-b border-slate-100"><div className="font-display text-xl font-semibold text-[#011E38]" data-testid="report-heading">{d.title}</div><div className="text-sm text-slate-500">{d.subtitle}</div></div>
          <table className="w-full text-left text-sm" data-testid="report-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500"><tr>{d.headers.map((h, i) => <th key={h} className={`px-4 py-3 whitespace-nowrap ${i ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-50">{r.map((c, j) => <td key={j} className={`px-4 py-3 whitespace-nowrap ${j ? "text-right num" : "font-medium text-[#011E38]"}`}>{typeof c === "number" ? c.toLocaleString("en-IN") : c}</td>)}</tr>
              ))}
            </tbody>
          </table>
          {!d.rows.length && <Empty>No data for this period.</Empty>}
        </div>
      )}
    </div>
  );
}
